import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Avatar, Col, Row, Skeleton, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { DownloadOutlined, UserAddOutlined } from "@ant-design/icons";

import axios from "axios";
import { itemActions } from "../../item/duck/itemReducer";
import { paramsToString, showNotification } from "../../utils/commonFunctions";
import constants from "../../utils/constants";
import fileDownload from "js-file-download";
import "./ProjectTaskList.scss";
import AppUrl from "../../config/appUrl";
import moment from "moment-timezone";
import ProjectTaskItem from "./ProjectTaskItem";
import NameWithAvatar from "../../components/NameWithAvatar";
import User from "../../models/User";
import { useLocation } from "react-router-dom";

const { DEFAULT_PAGING, PROJECT_TASK_STATUS, ROLE_IDS } = constants;
const { COMPLETED, APPROVED } = PROJECT_TASK_STATUS;

const ProjectTaskList = (props) => {
  const {
    projectId,
    taskStatus,
    filter = {},
    isProjectEditable,
    handleModal = () => { },
    onBeforeNavigate, // ✅ NEW: received from ProjectDetails
    isClient
  } = props;
  const { pathname } = useLocation();

  const [boardKey, setBoardKey] = useState("{}");

  // ✅ NEW: ref to the scrollable .list-container div
  const listContainerRef = useRef(null);

  // ✅ prevents infinite scroll from firing during scroll restoration
  const isRestoringRef = useRef(false);

  // ✅ store latest list/meta/loadMore in refs so the restore useEffect
  // can read fresh values without adding them to its dependency array
  const listRef = useRef([]);
  const metaRef = useRef(null);
  const loadMoreRef = useRef(null);

  // ✅ unique session keys per project + column (taskStatus)
  const scrollKey = `scroll_pos_${projectId}_${taskStatus}`;
  const scrollItemKey = `scroll_id_${projectId}_${taskStatus}`;

  const dispatch = useDispatch();
  const projectObj = useSelector(({ project }) => project?.map?.[projectId]);
  const project_name = useMemo(() => projectObj?.name, [projectObj?.name]);

  const assignedClient = useMemo(
    () => new User().fromMap(projectObj?.assigned_client),
    [projectObj?.assigned_client],
  );

  const loggedInUser = useSelector(({ loggedInUser }) => loggedInUser.data);

  const { list, meta, loading } = useSelector(({ item }) => {
    const mFilter = boardKey || JSON.stringify({});
    const boards = item.boards;
    const mBoard = boards[mFilter];

    return Object.assign({}, mBoard);
  });

  // ✅ keep refs in sync with latest values on every render (no dep array = runs every render)
  useEffect(() => {
    listRef.current = list || [];
    metaRef.current = meta;
  });

  const loadMore = useCallback(
    (offset = DEFAULT_PAGING.offset, limit = DEFAULT_PAGING.limit) => {
      if (offset === 0 || meta?.totalCount > list?.length) {
        const params = {
          pathname,
          project_id: projectId,
          status_description: taskStatus,
          offset,
          limit,
        };

        if (filter.task_name) params.task_name = filter.task_name;
        if (filter.start_date) {
          params.start_date = moment(filter.start_date)
            .startOf("day")
            .utc()
            .format();
        }
        if (filter.end_date) {
          params.end_date = moment(filter.end_date).endOf("day").utc().format();
        }
        if (filter.writer_id) params.writer_id = filter.writer_id;

        if (!filter.status_description) {
          if (PROJECT_TASK_STATUS.COMPLETED === taskStatus) {
            params.status_description = [COMPLETED, APPROVED];
          }
        } else if (filter.status_description === "approved") {
          if (PROJECT_TASK_STATUS.COMPLETED === taskStatus) {
            params.status_description = APPROVED;
          } else {
            params.status_description = "empty";
          }
        }

        setBoardKey(paramsToString(params, ["pathname"]));
        dispatch(itemActions.onGetAllRequest(params));
      }
    },
    [dispatch, list, pathname, meta, projectId, taskStatus, filter],
  );

  // ✅ keep loadMore ref in sync whenever it changes
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  useEffect(() => {
    loadMore();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ DECLARED BEFORE the restore useEffect that calls them
  // ✅ finds the item card in the DOM and scrolls the 80vh container to it
  const scrollToItem = useCallback(
    (itemId) => {
      requestAnimationFrame(() => {
        const container = listContainerRef.current;
        // each ProjectTaskItem card has data-item-id set on its root div
        const itemEl = container?.querySelector(`[data-item-id="${itemId}"]`);

        if (itemEl && container) {
          const containerTop = container.getBoundingClientRect().top;
          const itemTop = itemEl.getBoundingClientRect().top;
          const scrollOffset = itemTop - containerTop;

          // scroll to the item with a small top padding of 20px
          container.scrollTop = container.scrollTop + scrollOffset - 20;

          sessionStorage.removeItem(scrollKey);
          sessionStorage.removeItem(scrollItemKey);
          isRestoringRef.current = false;
        } else {
          // DOM not painted yet — retry on next animation frame
          requestAnimationFrame(() => scrollToItem(itemId));
        }
      });
    },
    [scrollKey, scrollItemKey],
  );

  // ✅ DECLARED BEFORE the restore useEffect that calls it
  // fallback: restore raw scrollTop when no item id is available
  const restoreScrollTop = useCallback(
    (savedScrollTop) => {
      if (!savedScrollTop) return;
      requestAnimationFrame(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTop = parseInt(savedScrollTop, 10);
          sessionStorage.removeItem(scrollKey);
          sessionStorage.removeItem(scrollItemKey);
          isRestoringRef.current = false;
        }
      });
    },
    [scrollKey, scrollItemKey],
  );

  // ✅ CORE restore effect
  // - depends only on [loading, scrollKey, scrollItemKey, scrollToItem, restoreScrollTop]
  // - reads list/meta/loadMore from refs → no stale closure, no ESLint warning
  useEffect(() => {
    if (loading) return;

    const currentList = listRef.current;
    const currentMeta = metaRef.current;

    if (!currentList?.length) return;

    const savedItemId = sessionStorage.getItem(scrollItemKey);
    const savedScrollTop = sessionStorage.getItem(scrollKey);

    if (!savedItemId && !savedScrollTop) return;

    isRestoringRef.current = true;

    if (savedItemId) {
      const itemExistsInList = currentList.includes(savedItemId);

      if (itemExistsInList) {
        // item is in the loaded list — scroll to it
        scrollToItem(savedItemId);
      } else if (currentMeta?.totalCount > currentList?.length) {
        // item not loaded yet — fetch next batch, effect re-runs on next loading=false
        loadMoreRef.current?.(currentList?.length);
      } else {
        // all pages fetched but item not found — fallback
        restoreScrollTop(savedScrollTop);
      }
    } else {
      restoreScrollTop(savedScrollTop);
    }
  }, [loading, scrollKey, scrollItemKey, scrollToItem, restoreScrollTop]);
  // ✅ list/meta/loadMore intentionally read from refs — no warning

  // ✅ infinite scroll — uses target.clientHeight (correct for fixed 80vh container)
  const handleScroll = useCallback(
    ({ target }) => {
      // block infinite scroll trigger while restoring position
      if (isRestoringRef.current) return;

      if (
        Math.ceil(target.scrollTop) + target.clientHeight >=
        target.scrollHeight - 10
      ) {
        if (!loading && list?.length < meta?.totalCount) {
          loadMore(list?.length);
        }
      }
    },
    [list?.length, loadMore, loading, meta?.totalCount],
  );

  const downloadTask = useCallback(() => {
    const mParams = JSON.parse(boardKey);
    axios({
      method: "GET",
      url: `${AppUrl.ITEMS}/export`,
      params: {
        project_name,
        ...mParams,
      },
      responseType: "arraybuffer",
    })
      .then((response) => {
        if (response.data) {
          let filename = response.headers["content-type"].split("=");
          fileDownload(response.data, filename[1]);
          showNotification("success", "File downloaded.");
        } else {
          showNotification("error", "Error while downloading file.");
        }
      })
      .catch((err) => {
        console.log(err);
        showNotification("error", "Something went wrong");
      });
  }, [boardKey, project_name]);

  // saves item id + raw scrollTop before navigating to detail page
  const handleBeforeNavigate = useCallback(
    (itemId) => {
      if (listContainerRef.current) {
        sessionStorage.setItem(
          scrollKey,
          String(listContainerRef.current.scrollTop),
        );
      }
      if (itemId) {
        sessionStorage.setItem(scrollItemKey, itemId);
      }
      onBeforeNavigate?.();
    },
    [scrollKey, scrollItemKey, onBeforeNavigate],
  );

  return (
    <Col className="project-task-list-container pri-box-shadow">
      {/* header start */}
      <Row
        className="header pri-box-shadow"
        align="top"
        justify="space-between"
      >
        <Row className="list-name" wrap={false}>
          {props.listName} ({meta?.totalCount || 0}) &nbsp;
          {taskStatus === PROJECT_TASK_STATUS.COMPLETED &&
            loggedInUser?.role_id === ROLE_IDS.SUPER_ADMIN && (
              <>
                {assignedClient?._id ? (
                  <NameWithAvatar
                    user={assignedClient}
                    onClick={() => handleModal(true)}
                    avatarOnly
                  />
                ) : (
                  isProjectEditable && (
                    <Tooltip title="Assign Client">
                      <Avatar
                        className="btn-select-manager pointer"
                        icon={<UserAddOutlined />}
                        onClick={() => handleModal(true)}
                      />
                    </Tooltip>
                  )
                )}
              </>
            )}
          <span className="subheading">{props.subHeading}</span>
        </Row>

        <Row wrap={false}>
          {meta?.totalCount > 0 && (
            <Col className="ml5">
              <Tooltip title="Download as Excel" placement="bottomLeft">
                <DownloadOutlined
                  style={{ fontSize: 18, marginLeft: "auto" }}
                  onClick={downloadTask}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      </Row>
      {/* header end */}

      {/* task list body container start */}
      <Col className="body">
        {!list?.length && loading ? (
          <Col className="project-task-kanban-item-container m15">
            <Skeleton active={loading} paragraph={{ rows: 15 }} />
          </Col>
        ) : (
          <Col
            className="list-container"
            ref={listContainerRef}
            onScroll={handleScroll}
          >
            {list?.map((item, i) => (
              <ProjectTaskItem
                key={i}
                id={item}
                projectId={projectId}
                isProjectEditable={isProjectEditable}
                onBeforeNavigate={handleBeforeNavigate} // ✅ NEW: pass down to item
                isClient={isClient} // ✅ pass down

              />
            ))}
            {!!list?.length && loading && (
              <Col className="project-task-kanban-item-container m15">
                <Skeleton active={loading} paragraph={{ rows: 2 }} />
              </Col>
            )}
          </Col>
        )}
      </Col>
      {/* task list body container end */}
    </Col>
  );
};;;;;;;

export default React.memo(ProjectTaskList);
